import * as React from 'react'
import { ReactElement, useMemo } from 'react'
import { Button, Toast, ToastBody, ToastFooter, ToastTitle } from '@fluentui/react-components'
import { DocumentGenerationContextData, DocumentGenerationStatusData } from '../../hooks/document/documentGenerationTypes'
import { stealthClick } from '../StealthLink'
import useAxios from '../../hooks/useAxios'
import { ArrowDownloadFilled, ArrowDownloadRegular, bundleIcon, OpenFilled, OpenRegular } from '@fluentui/react-icons'
import ErrorPopover from './ErrorPopover'
import QueueToastActions from './QueueToastActions'
import { QueueToastContentProps } from './queueToastTypes'
import { QueueData, QueueStatus } from '../../hooks/queue/queueTypes'
import { useTranslation } from 'react-i18next'
import ToastDismiss from './ToastDismiss'
import { getDocumentType, OfficeUriMap } from '../../helpers/documentHelper'
import useDocumentsOpen from '../../hooks/document/useDocumentsOpen'

const DownloadIcon = bundleIcon(ArrowDownloadFilled, ArrowDownloadRegular)
const OpenIcon = bundleIcon(OpenFilled, OpenRegular)

type DocumentQueueData = QueueData<unknown, DocumentGenerationStatusData, DocumentGenerationContextData>

function getDocumentToastMessage({ status, context }: DocumentQueueData, translate: (key: string, options?: any) => string) {
    const type = context.generationType.toLowerCase().replaceAll(/[-_]/g, '')
    const label = translate(`toast.document.type.${type}.title`)
    const determinerLC = translate(`toast.document.type.${type}.determiner`)
    const noun = translate(`toast.document.type.${type}.noun`)
    const determinerUC = determinerLC.charAt(0).toUpperCase() + determinerLC.slice(1)

    const title = context.title || label

    return new Map<QueueStatus, ReactElement | string>([
        [
            QueueStatus.Dispatch,
            translate('toast.document.dispatch', {
                determinerLC: determinerLC,
                determinerUC: determinerUC,
                noun: noun,
                title: title,
            }),
        ],
        [
            QueueStatus.Queued,
            translate('toast.document.queued', {
                determinerLC: determinerLC,
                determinerUC: determinerUC,
                noun: noun,
                title: title,
            }),
        ],
        [
            QueueStatus.Delayed,
            translate('toast.document.delayed', {
                determinerLC: determinerLC,
                determinerUC: determinerUC,
                noun: noun,
                title: title,
            }),
        ],
        [
            QueueStatus.Cancelled,
            translate('toast.document.cancelled', {
                determinerLC: determinerLC,
                determinerUC: determinerUC,
                noun: noun,
                title: title,
            }),
        ],
        [
            QueueStatus.Failed,
            translate('toast.document.failed', {
                determinerLC: determinerLC,
                determinerUC: determinerUC,
                noun: noun,
                title: title,
            }),
        ],
        [
            QueueStatus.Finished,
            translate('toast.document.finished', {
                determinerLC: determinerLC,
                determinerUC: determinerUC,
                noun: noun,
                title: title,
            }),
        ],
        [
            QueueStatus.Duplicate,
            translate('toast.document.duplicate', {
                determinerLC: determinerLC,
                determinerUC: determinerUC,
                noun: noun,
                title: title,
            }),
        ],
    ]).get(status.current)
}

export default function DocumentToastContent(props: QueueToastContentProps): ReactElement {
    const axios = useAxios()
    const { t: translate } = useTranslation()
    const documentsOpen = useDocumentsOpen()

    const job = props.job as DocumentQueueData
    const driveItem = job.status.driveItem
    const isOffice: boolean = useMemo(() => OfficeUriMap.has(getDocumentType(driveItem?.name)), [driveItem])

    return (
        <Toast>
            <ToastTitle action={<ToastDismiss />}>{getDocumentToastMessage(job, translate)}</ToastTitle>
            {job.status.error && (
                <ToastBody>
                    <ErrorPopover error={job.status.error} />
                </ToastBody>
            )}
            <ToastFooter>
                {driveItem && (
                    <Button
                        icon={isOffice ? <OpenIcon /> : <DownloadIcon />}
                        appearance="subtle"
                        onClick={() => stealthClick({ id: driveItem.id, item: driveItem, documentsOpen: documentsOpen }, axios)}
                    >
                        {isOffice ? translate('toast.open') : translate('toast.download')}
                    </Button>
                )}
                <QueueToastActions id={props.id} job={props.job} />
            </ToastFooter>
        </Toast>
    )
}
